// The live model has no server-side voice activity detection, so the client
// decides where a dictated phrase ends. Times are seconds of recorded audio.
export type TurnState = {
  start: number;
  duration: number;
  speech: number;
  silence: number;
};

export type TurnDecision = 'continue' | 'commit' | 'discard';

export type TurnConfig = {
  // RMS on a 0..1 scale; about -36 dBFS, above a quiet room's noise floor.
  speechLevel: number;
  // A pause this long after speech ends the phrase.
  endSilence: number;
  // Shorter bursts (a cough, a click) are not worth a transcription request.
  minSpeech: number;
  // Long monologues are still split so text keeps arriving.
  maxDuration: number;
  // Audio without any speech is dropped after this long.
  idleDuration: number;
};

export const LIVE_TURN_CONFIG: TurnConfig = {
  speechLevel: 0.015,
  endSilence: 0.8,
  minSpeech: 0.3,
  maxDuration: 15,
  idleDuration: 8,
};

export const startTurn = (start: number): TurnState => ({
  start,
  duration: 0,
  speech: 0,
  silence: 0,
});

export const advanceTurn = ({
  turn,
  seconds,
  level,
  config = LIVE_TURN_CONFIG,
}: {
  turn: TurnState;
  seconds: number;
  level: number;
  config?: TurnConfig;
}): { turn: TurnState; decision: TurnDecision } => {
  const speaking = level >= config.speechLevel;
  const next: TurnState = {
    start: turn.start,
    duration: turn.duration + seconds,
    speech: turn.speech + (speaking ? seconds : 0),
    silence: speaking ? 0 : turn.silence + seconds,
  };
  const hasSpeech = next.speech >= config.minSpeech;
  if (hasSpeech && next.silence >= config.endSilence) {
    return { turn: next, decision: 'commit' };
  }
  if (next.duration >= config.maxDuration) {
    return { turn: next, decision: hasSpeech ? 'commit' : 'discard' };
  }
  if (!next.speech && next.duration >= config.idleDuration) {
    return { turn: next, decision: 'discard' };
  }
  return { turn: next, decision: 'continue' };
};

// Pause and stop close the current phrase without waiting for silence.
export const finishTurn = (
  turn: TurnState,
  config: TurnConfig = LIVE_TURN_CONFIG,
): TurnDecision => (turn.speech >= config.minSpeech ? 'commit' : 'discard');
